import { useState } from "react";
import {
  get_db_base,
  getLocalizedOrDefault,
  request_items
} from "../../../../helpers/Functions";
import { getImageUrl } from "../../../../helpers/PB";
import AudiencesConfig from "../../../../shared/schema/comparisontool/Audiences";
import BrandConfig from "../../../../shared/schema/comparisontool/Brand";
import ContactConfig from "../../../../shared/schema/comparisontool/Contact";
import CurrencyConfig from "../../../../shared/schema/comparisontool/Currency";
import DisclaimerConfig from "../../../../shared/schema/comparisontool/Disclaimer";
import FamilyConfig from "../../../../shared/schema/comparisontool/Family";
import PriceTypeConfig from "../../../../shared/schema/comparisontool/PriceType";
import ProductTypeConfig from "../../../../shared/schema/comparisontool/ProductType";
import SpecificationStyleConfig from "../../../../shared/schema/comparisontool/SpecificationStyle";
import ClientInformationConfig from "./collections/ClientInformation";
import ConversionFactorsConfig from "./collections/ConversionFactors";
import ElectricityProfilesConfig from "./collections/ElectricityProfiles";
import FleetsConfig from "./collections/Fleets";
import InformationConfig from "./collections/Information";
import MiscellaneousConfig from "./collections/Miscellaneous";
import PreviewExportConfig from "./collections/PreviewExport";
import ProductsConfig from "./collections/Products";
import ReportsConfig from "./collections/Reports";
import ReportTemplateConfig from "./collections/ReportTemplate";
import ReportTypesConfig from "./collections/ReportTypes";
import TecPpmLookupConfig from "./collections/TecPpmLookup";

// bij-energy translation tables use `code` as the locale FK column.
// Strip the old `language` / `languages_code` field paths from any shared
// schema and replace them with the correct `locale.code` field.
const _staleLocaleFields = [
  "locale.language",
  "locale.languages_code.code",
  "locale.languages_code.*"
];

// Fields present in other projects' schemas but absent from bij-energy.
const _bijEnergyUnsupportedFields = [
  // disclaimers collection
  "always_show_on"
];

function forBijEnergy(config) {
  const hasStale = config.fields.some((f) => _staleLocaleFields.includes(f));
  const hasUnsupported = config.fields.some((f) =>
    _bijEnergyUnsupportedFields.includes(f)
  );
  if (!hasStale && !hasUnsupported) return config;

  let fields = config.fields
    .filter((f) => !_staleLocaleFields.includes(f))
    .filter((f) => !_bijEnergyUnsupportedFields.includes(f));

  if (hasStale) fields = [...fields, "locale.code"];

  if (!hasStale) {
    return { ...config, fields };
  }

  const OriginalAdapter = config.adapter;
  class BijEnergyAdapter extends OriginalAdapter {
    constructor(data) {
      super({
        ...data,
        locale: Array.isArray(data.locale)
          ? data.locale.map((loc) =>
              loc.code && !loc.languages_code
                ? { ...loc, languages_code: { code: loc.code } }
                : loc
            )
          : data.locale
      });
    }
  }

  return { ...config, fields, adapter: BijEnergyAdapter };
}

// The `languages` collection uses `code` as its PK; it no longer has a
// `language` field. Build a bij-energy-specific locale config accordingly.
const BijEnergyLocaleConfig = {
  collection: "languages",
  fields: ["code", "currency", "name"],
  adapter: class Locale {
    constructor(locale) {
      Object.assign(this, { ...locale, id: locale.code, language: locale.code });
    }
    map(collections, maps) {
      this.currency = maps.currencies[this.currency];
    }
  }
};

// bij-energy's global_settings only needs a small subset of the shared schema's
// fields — many comparison-tool fields don't exist in this project's DB.
const BijEnergyGlobalSettingsConfig = {
  collection: "global_settings",
  single: true,
  fields: [
    "id",
    "filename",
    "primary_brand.id",
    "default_locale.code",
    "logo.id",
    "logo.title",
    "modified_on",
    "created_on",
    "locale.code",
    "locale.page_title"
  ],
  adapter: class GlobalSettings {
    constructor(settings) {
      this.id = settings.id;
      this.date_updated = settings.modified_on || settings.created_on;
      this.filename = settings.filename;
      this.default_locale = settings.default_locale?.code;
      this.logo_url = getImageUrl(settings.logo?.id);
      this.logo_title = settings.logo?.title;
      this.primary_brand_id = settings.primary_brand?.id;
      this.locale = settings.locale || [];
    }
    map(collections, maps) {
      this.primary_brand = maps.brands[this.primary_brand_id];
      this.default_locale = maps.languages?.[this.default_locale];
      this.locale = this.locale.map(({ code, ...other_props }) => ({
        ...other_props,
        language: maps.languages?.[code]
      }));
      this.getLocalized = (field_name, selected_locale) =>
        getLocalizedOrDefault(
          this,
          field_name,
          { locale: selected_locale },
          { locale: this.default_locale?.language || this.default_locale }
        );
    }
  }
};

function map_collection_items_by_id(collection, items) {
  let obj = {};
  obj.map = {};
  items.forEach((item) => (obj.map[item.id] = item));
  return obj;
}

const collateProjectData = (project, project_data) => {
  // Extract arrays and maps to array elements
  let collections = {
    _global: { project }
  };
  let collections_unmapped = {};
  let collections_maps = {};
  let collection_names = [];
  project_data.forEach((collection_info) => {
    collection_names.push(collection_info.name);
    collections[collection_info.name] = collection_info.data;
    collections_unmapped[collection_info.name] = collection_info.unmapped;
    collections_maps[collection_info.name] = collection_info.map;
  });

  // Perform mappings
  collection_names.forEach((collection_name) => {
    let next_collection = collections[collection_name];
    if (Array.isArray(next_collection))
      next_collection.forEach((item) =>
        item.map?.(collections, collections_maps)
      );
    else next_collection.map?.(collections, collections_maps);
  });

  return {
    collections,
    collections_maps,
    collection_names,
    collections_unmapped,
    config: {
      current_project: project
    }
  };
};

const useProjectData = (project) => {
  const [projectData, setPd] = useState(null);
  //eslint-disable-next-line
  const [pdLoading, setPdLoading] = useState(false);
  //eslint-disable-next-line
  const [pdError, setPdError] = useState(false);
  const db_base = get_db_base(project);

  const preview_collections_config = [
    BijEnergyGlobalSettingsConfig,
    BrandConfig,
    SpecificationStyleConfig,
    PriceTypeConfig,
    ContactConfig,
    CurrencyConfig,
    BijEnergyLocaleConfig,
    AudiencesConfig,
    forBijEnergy(ProductTypeConfig),
    FamilyConfig,
    forBijEnergy(DisclaimerConfig),
    ClientInformationConfig,
    FleetsConfig,
    MiscellaneousConfig,
    PreviewExportConfig,
    ReportsConfig,
    ReportTemplateConfig,
    ReportTypesConfig,
    InformationConfig,
    ProductsConfig,
    ElectricityProfilesConfig,
    TecPpmLookupConfig,
    ConversionFactorsConfig
  ];

  function request(
    collection,
    collection_name_override,
    is_single,
    fields_to_get,
    filter
  ) {
    return request_items(
      db_base,
      collection,
      collection_name_override,
      is_single,
      fields_to_get,
      filter
    );
  }

  function loadType(collection, fields, type, single, filter) {
    return new Promise((accept, reject) => {
      request(collection, null, single, fields, filter)
        .then((items) => {
          let obj = { name: collection };
          if (single) {
            obj.data = new type(items);
            obj.unmapped = items;
          } else {
            obj.data = items.map((item) => new type(item));
            obj.unmapped = items;
            let mapped_data = map_collection_items_by_id(collection, obj.data);
            obj = { ...obj, ...mapped_data };
          }

          accept(obj);
        })
        .catch((e) => reject(e));
    });
  }

  const load = (collection_configs) => {
    let promises = collection_configs.map(
      ({ collection, fields, adapter, single, filter }) =>
        loadType(collection, fields, adapter, single, filter)
    );

    return Promise.all(promises).then((project_data) => {
      const collated_data = collateProjectData(project, project_data);
      setPd(collated_data);
      return collated_data;
    });
  };

  const loadPreview = () => load(preview_collections_config);

  const status = { projectData, pdLoading, pdError };
  const actions = { loadPreview };
  return [status, actions];
};

export default useProjectData;
